import Papa from "papaparse";

const defaultColumnNum = 7;

/**
 * Restituisce la regular expression per matchare l'embedded CSV all'interno della descrizione prodotto
 * racchiusa dalla sintassi:
 *    "descrizione prodotto |*** ...csv... ***| altro"
 *
 * Opzionalmente con la seguente sintassi è possibile specificare il numero di colonne del csv:
 *    "descrizione prodotto |{12}*** ...csv... ***| altro"
 *
 * Nel caso in cui il csv non contenesse già i delimitatori di riga ("\n"), questi verranno aggiunti ogni tot colonne (default: 7)
 */
const getEmbeddedCSVRegEx = () => {
  return /\|(?:\{(\d+)\})?\*\*\*([\s\S]*)\*\*\*\|/gm;
}

/**
 * Restituisce la regular expression per individuare le righe all'interno del csv.
 * Ogni tot celle (indicato da columnNum) viene individuato il delimitatore che rappresenta la fine della riga.
 */
const getNewLineRegEx = (columnNum: number, delimiter: string = ';') => {
  return new RegExp(`((?:[^${delimiter}]*${delimiter}){${(columnNum-1)}})([^${delimiter}]*)(${delimiter})`, 'gm');
}

/**
 * Rimuove dal parametro description le eventuali porzioni contenenti embedded CSV e restituisce la stringa rimanente.
 * Se il parametro description è nullish, ritorna description stessa, altrimenti ritorna una stringa.
 *
 * Es: usato per la longDescription in PDP.
 */
export const stripEmbeddedCSV = (description: string | undefined): string | undefined => {
  if (description) {
    const embeddedCSVRegEx = getEmbeddedCSVRegEx();
    return description.replaceAll(embeddedCSVRegEx, '');
  } else {
    return description;
  }
}

/**
 * Estrae e restituisce dal parametro description l'eventuale embedded CSV.
 * Dato che le andate a capo sono state perse (forse dal PIM) vengono riaggiunte qui, ogni tot colonne.
 * Se il parametro description è nullish, ritorna description stessa, altrimenti ritorna una stringa.
 *
 * Es: usato per la longDescription in PDP.
 */
export const extractEmbeddedCSV = (description: string | undefined): string | undefined => {
  if (description) {
    const embeddedCSVRegEx = getEmbeddedCSVRegEx();
    const matches = embeddedCSVRegEx.exec(description);
    if (matches && matches.length === 3) {

      // Estraggo il numero di colonne per riga dall'embedd syntax.
      // Es: |{12}*** .... ***|
      //   indica che il csv originariamente aveva una new line ogni 12 colonne
      // se non è presente (Es: |*** ... ***|) viene usato il valore defaultColumnNum (7)
      const columnNum: number = matches[1] !== undefined ? parseInt(matches[1]) : defaultColumnNum;

      // Estraggo il testo del CSV
      const rawContent: string = matches[2];

      // Faccio un parsing preliminare per sapere se il CSV è valido e quale delimitatore usa
      const parsedCSVTemp = Papa.parse(rawContent);
      if (parsedCSVTemp?.meta?.aborted) {
        return undefined;
      }

      // Se necessario reintroduco le andate a capo
      let multilineContent = rawContent;
      if (rawContent.includes('\\n')) {
        multilineContent = multilineContent.replace(/^\\n/, '');
        multilineContent = multilineContent.replaceAll('\\n', '\n');

      } else {
        const newLineRegEx = getNewLineRegEx(columnNum, parsedCSVTemp?.meta?.delimiter);
        multilineContent = rawContent.replaceAll(newLineRegEx, (_match, p1, p2, _p3, _offset, _string, _groups) => {
          const ret = [p1, p2, parsedCSVTemp?.meta?.linebreak].join('');
          return ret;
        });
      }

      return multilineContent;
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
}

/**
 * Data la stringa description contenente anche l'embedded CSV con sintassi |*** ... ***|
 * estrare il csv, riaggiunge se serve le andate a capo e lo parsa con la libreria papaparse
 * restituendo il json corrispondente.
 */
export const parseEmbeddedCSV = (description: string | undefined) => {
  const text = extractEmbeddedCSV(description);
  if (text) {
    const parsedCSV = Papa.parse(text, {
      header: true,
    });
    return parsedCSV;
  } else {
    return undefined;
  }
}
